import { Flex, Tooltip, Typography } from "antd";
import {
  ChangeBankAccountButton,
  formatCurrencyOperationCodesForDisplay,
  parseCurrencyOperationCodesFromApi,
} from "components";
import { BID_REQUEST_STATUS_ENUM, BidEntity, ItemInfoType } from "interfaces";
import { UserStore } from "stores/UserStore";
import styles from "./bidInfo.module.css";
import { CurrencyCodeEditBtn } from "../CurrencyCodeEditBtn/CurrencyCodeEditBtn";
import { isStatusIncluded } from "utils";

interface BankAccountInfoProps {
  bankName?: string;
  accountName?: string;
  canChange: boolean;
  bankAccountId?: string;
  showAccountName: boolean;
  withTooltip?: boolean;
}

const BankAccountInfo = ({
  bankName,
  accountName,
  canChange,
  bankAccountId,
  showAccountName,
  withTooltip = false,
}: BankAccountInfoProps) => {
  const textContent = (
    <Flex vertical flex={1} className={`overflow-hidden ${canChange ? styles.constrainedText : ""}`}>
      <Typography.Text className={`text-middle text-clamp-ellipsis`}>{bankName}</Typography.Text>
      {showAccountName && accountName && (
        <Typography.Text type="secondary" className="text-clamp-ellipsis">
          {accountName}
        </Typography.Text>
      )}
    </Flex>
  );

  return (
    <Flex gap="small" className="full-width" align="start">
      {withTooltip ? <Tooltip title={bankName}>{textContent}</Tooltip> : textContent}
      {canChange && (
        <Flex align="center" className={`${styles.changeBankButton} flex-shrink-0`}>
          <ChangeBankAccountButton bankAccountId={bankAccountId} />
        </Flex>
      )}
    </Flex>
  );
};

export const getBankColumns = (bid: BidEntity, user: UserStore): Array<ItemInfoType> => {
  const { customerBank, recipientAccount, platformAccount, currencyOperationCode } = bid;

  const {
    isOperator,
    isProvider,
    isSeniorCustomer,
    isSeniorLP,
    isJuniorLP,
    isLPRiskManager,
    isCustomer,
    isSeniorOperator,
  } = user.roles;

  const isLpProvider = isJuniorLP || isSeniorLP || isLPRiskManager;
  const isDirectPaymentProviderLp = isLpProvider && process.env.REACT_APP_DIRECT_PAYMENT_CUSTOMER_LP === "true";

  const showCustomerBank = customerBank && (isOperator || isProvider || isLPRiskManager);
  const showRecipientBank = recipientAccount && isOperator;
  const showPlatformBank = isOperator && platformAccount;

  const canChangeRecipientBank = isSeniorCustomer || isSeniorLP;
  const canChangePlatformBank = isOperator && (bid.status === "DRAFT" || bid.status === "AGENT_PROPOSAL");
  const showRecipientAccountName =
    canChangeRecipientBank && recipientAccount && recipientAccount.bankName !== recipientAccount.accountName;

  const canChangeCurrencyOperationCode =
    (isCustomer &&
      isStatusIncluded(bid.status, [
        BID_REQUEST_STATUS_ENUM.DRAFT,
        BID_REQUEST_STATUS_ENUM.DRAFT_APPROVED,
        BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL,
        BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL_ACCEPTED,
        BID_REQUEST_STATUS_ENUM.NEW,
      ])) ||
    isSeniorOperator;

  const bankColumns: ItemInfoType[] = [
    {
      label: "Код вида операции",
      text: (
        <Flex gap="small">
          {currencyOperationCode ? (
            parseCurrencyOperationCodesFromApi(currencyOperationCode).map((code, index) => (
              <Tooltip title={formatCurrencyOperationCodesForDisplay(code)} key={index}>
                <Typography.Text className="text-width-30ch text-clamp-ellipsis text-middle">
                  {code}
                  {index < parseCurrencyOperationCodesFromApi(currencyOperationCode).length - 1 ? "," : ""}
                </Typography.Text>
              </Tooltip>
            ))
          ) : (
            <Typography.Text className="text-width-30ch text-clamp-ellipsis text-middle">—</Typography.Text>
          )}
          {canChangeCurrencyOperationCode && <CurrencyCodeEditBtn />}
        </Flex>
      ),
    },
    showCustomerBank &&
      isDirectPaymentProviderLp && {
        label: "Банк УВЭД",
        text: (
          <Flex>
            <Typography.Text className={`text-width-30ch text-clamp-ellipsis text-middle`}>
              {customerBank}
            </Typography.Text>
          </Flex>
        ),
      },
    ((showRecipientBank && !isLpProvider) || isDirectPaymentProviderLp) && {
      label: "Банк получателя",
      text: (
        <BankAccountInfo
          bankName={recipientAccount?.bankName}
          accountName={recipientAccount?.accountName}
          canChange={canChangeRecipientBank}
          bankAccountId={recipientAccount?.id}
          showAccountName={showRecipientAccountName || false}
        />
      ),
    },
    showPlatformBank && {
      label: "Банк платформы",
      text: (
        <BankAccountInfo
          bankName={platformAccount?.bankName}
          accountName={platformAccount?.accountName}
          canChange={canChangePlatformBank}
          bankAccountId={platformAccount?.id}
          showAccountName={platformAccount?.bankName !== platformAccount?.accountName}
          withTooltip
        />
      ),
    },

    // isOperator && {
    //   label: "Банк платформы",
    //   text: (
    //     <Typography.Text type="secondary" className={`text-width-30ch text-clamp-ellipsis`}>
    //       {recipientAccount?.accountName}
    //     </Typography.Text>
    //   ),
    // },
  ].filter((c) => !!c) as ItemInfoType[];

  return bankColumns;
};
